"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_ORDER_VARIABLE_PATHS = exports.ORDER_VARIABLE_NAMESPACE = exports.ORDER_VARIABLE_GROUP_LABELS = exports.orderVariableGroupSchema = exports.ORDER_VARIABLE_GROUPS = void 0;
const zod_1 = require("zod");
// Variable namespace for order-hosted Sales Order / Purchase Order templates.
// Derived from the backend's OrderRenderContext (order-render-context.ts) —
// the exact shape OrderContextBuilder.build() returns at production render
// time — so the picker only offers variables that genuinely resolve.
// ORDER_PREVIEW_FIXTURE must define a value for every path listed here
// (enforced by order-preview-fixture.spec.ts).
exports.ORDER_VARIABLE_GROUPS = [
    'metycle',
    'order_details',
    'buyer',
    'supplier',
    'sell_terms',
    'buy_terms',
];
exports.orderVariableGroupSchema = zod_1.z.enum(exports.ORDER_VARIABLE_GROUPS);
exports.ORDER_VARIABLE_GROUP_LABELS = {
    metycle: 'Metycle Company Info',
    order_details: 'Order Details',
    buyer: 'Buyer (Sales Order)',
    supplier: 'Supplier (Purchase Order)',
    sell_terms: 'Sell-Side Terms (Sales Order)',
    buy_terms: 'Buy-Side Terms (Purchase Order)',
};
exports.ORDER_VARIABLE_NAMESPACE = {
    metycle: {
        repeating: false,
        variables: [
            { path: 'metycle_name', label: 'Company Name' },
            { path: 'metycle_address', label: 'Address' },
            { path: 'metycle_city', label: 'City' },
            { path: 'metycle_country', label: 'Country' },
            { path: 'metycle_phone', label: 'Phone' },
            { path: 'metycle_email', label: 'Email' },
            { path: 'metycle_managing_directors', label: 'Managing Directors' },
            { path: 'metycle_register', label: 'Company Register' },
            { path: 'metycle_vat_id', label: 'VAT ID' },
            { path: 'metycle_bank_1', label: 'Bank 1 Name' },
            { path: 'metycle_iban_1', label: 'Bank 1 IBAN' },
            { path: 'metycle_bic_1', label: 'Bank 1 BIC' },
            { path: 'metycle_bank_2', label: 'Bank 2 Name' },
            { path: 'metycle_iban_2', label: 'Bank 2 IBAN' },
            { path: 'metycle_bic_2', label: 'Bank 2 BIC' },
            { path: 'metycle_signatory_name', label: 'Metycle Signatory (trader)' },
            { path: 'current_date', label: 'Current Date' },
        ],
    },
    order_details: {
        repeating: false,
        variables: [
            { path: 'order_id', label: 'Order ID' },
            { path: 'commodity_type', label: 'Commodity Type' },
            { path: 'isri_grade', label: 'ISRI Grade' },
            { path: 'metal_form', label: 'Metal Form' },
            { path: 'product_details', label: 'Product Details' },
            { path: 'quantity_mt', label: 'Quantity (MT)' },
            { path: 'ship_from', label: 'Ship From' },
            { path: 'ship_to', label: 'Ship To' },
            { path: 'shipping_description', label: 'Shipping Description' },
            { path: 'country_of_origin', label: 'Country of Origin' },
            { path: 'additional_remarks', label: 'Additional Remarks' },
            // Opt-in operator line; carries its own label so it hides when left blank.
            { path: 'final_destination_line', label: 'Final Destination' },
            // Computed commercial-term lines — order-derived, shared (hide when empty)
            { path: 'quality_line', label: 'Quality Line (computed)' },
            { path: 'packing_line', label: 'Packing Line (computed)' },
            { path: 'partial_shipment_line', label: 'Partial Shipment Line (computed)' },
        ],
    },
    buyer: {
        repeating: false,
        variables: [
            { path: 'buyer_name', label: 'Buyer Name' },
            { path: 'buyer_address', label: 'Buyer Address' },
            { path: 'buyer_city', label: 'Buyer City' },
            { path: 'buyer_country', label: 'Buyer Country' },
            { path: 'buyer_vat_id', label: 'Buyer VAT ID' },
            { path: 'buyer_contact_name', label: 'Buyer Contact Name' },
            { path: 'buyer_contact_email', label: 'Buyer Contact Email' },
            { path: 'buyer_vat_line', label: 'Buyer VAT/GSTIN Line (computed)' },
            { path: 'buyer_pan_line', label: 'Buyer PAN Line (computed)' },
            { path: 'buyer_iec_line', label: 'Buyer IEC Line (computed)' },
        ],
    },
    supplier: {
        repeating: false,
        variables: [
            { path: 'supplier_name', label: 'Supplier Name' },
            { path: 'supplier_address', label: 'Supplier Address' },
            { path: 'supplier_city', label: 'Supplier City' },
            { path: 'supplier_country', label: 'Supplier Country' },
            { path: 'supplier_vat_id', label: 'Supplier VAT ID' },
            { path: 'supplier_contact_name', label: 'Supplier Contact Name' },
            { path: 'supplier_contact_email', label: 'Supplier Contact Email' },
            { path: 'supplier_vat_line', label: 'Supplier VAT/GSTIN Line (computed)' },
            { path: 'supplier_pan_line', label: 'Supplier PAN Line (computed)' },
            { path: 'supplier_iec_line', label: 'Supplier IEC Line (computed)' },
        ],
    },
    sell_terms: {
        repeating: false,
        variables: [
            { path: 'sell_price', label: 'Sell Price' },
            { path: 'sell_currency', label: 'Sell Currency' },
            { path: 'sell_incoterms', label: 'Sell Incoterms' },
            { path: 'sell_payment_terms', label: 'Sell Payment Terms' },
            { path: 'sell_pricing_type', label: 'Sell Pricing Type' },
            { path: 'sell_transport_method', label: 'Sell Transport Method' },
            { path: 'sell_qp_start', label: 'Sell QP Start' },
            { path: 'sell_qp_end', label: 'Sell QP End' },
            { path: 'sell_total_value', label: 'Sell Total Value' },
            { path: 'sell_signatory_name', label: 'Sell Signatory Name' },
            { path: 'sell_unit_price_description', label: 'Sell Unit Price Description (computed)' },
            { path: 'sell_product_lines_html', label: 'Sell Product Lines (computed HTML)' },
            { path: 'sell_quotation_period_line', label: 'Sell Quotation Period Line (computed)' },
            { path: 'sell_destination_line', label: 'Sell Destination Line (computed)' },
            { path: 'sell_shipment_period_line', label: 'Sell Shipment Period Line (computed)' },
            { path: 'sell_port_of_loading_line', label: 'Sell Port of Loading Line (computed)' },
        ],
    },
    buy_terms: {
        repeating: false,
        variables: [
            { path: 'buy_price', label: 'Buy Price' },
            { path: 'buy_currency', label: 'Buy Currency' },
            { path: 'buy_incoterms', label: 'Buy Incoterms' },
            { path: 'buy_payment_terms', label: 'Buy Payment Terms' },
            { path: 'buy_pricing_type', label: 'Buy Pricing Type' },
            { path: 'buy_transport_method', label: 'Buy Transport Method' },
            { path: 'buy_qp_start', label: 'Buy QP Start' },
            { path: 'buy_qp_end', label: 'Buy QP End' },
            { path: 'buy_total_value', label: 'Buy Total Value' },
            { path: 'buy_signatory_name', label: 'Buy Signatory Name' },
            { path: 'buy_unit_price_description', label: 'Buy Unit Price Description (computed)' },
            { path: 'buy_product_lines_html', label: 'Buy Product Lines (computed HTML)' },
            { path: 'buy_quotation_period_line', label: 'Buy Quotation Period Line (computed)' },
            { path: 'buy_destination_line', label: 'Buy Destination Line (computed)' },
            { path: 'buy_shipment_period_line', label: 'Buy Shipment Period Line (computed)' },
            { path: 'buy_port_of_loading_line', label: 'Buy Port of Loading Line (computed)' },
        ],
    },
};
exports.ALL_ORDER_VARIABLE_PATHS = Object.values(exports.ORDER_VARIABLE_NAMESPACE).flatMap((group) => group.variables.map((v) => v.path));
//# sourceMappingURL=order-variable-namespace.js.map